import { PARAM_KEY, getBackgroundColorByName, ADDON_ID } from './chunk-VS46XGVE.mjs';
import React, { memo, useState, useMemo, useCallback, Fragment } from 'react';
import { useParameter, useGlobals, addons, types } from '@storybook/manager-api';
import memoize from 'memoizerific';
import { logger } from '@storybook/client-logger';
import { WithTooltip, TooltipLinkList, IconButton } from '@storybook/components';
import { Icon } from '@storybook/components/experimental';
import { styled } from '@storybook/theming';

var ColorIcon = styled.span(
  ({ background }) => ({
    borderRadius: "1rem",
    display: "block",
    height: "1rem",
    width: "1rem",
    background
  }),
  ({ theme }) => ({
    boxShadow: `${theme.appBorderColor} 0 0 0 1px inset`
  })
);

// src/containers/BackgroundSelector.tsx
var createBackgroundSelectorItem = memoize(1e3)(
  (id, name, value, hasSwatch, change, active) => ({
    id: id || name,
    title: name,
    onClick: () => {
      change({ selected: value, name });
    },
    value,
    right: hasSwatch ? /* @__PURE__ */ React.createElement(ColorIcon, { background: value }) : void 0,
    active
  })
);
var getDisplayedItems = memoize(10)(
  (backgrounds, selectedBackgroundColor, change) => {
    const backgroundSelectorItems = backgrounds.map(
      ({ name, value }) => createBackgroundSelectorItem(
        null,
        name,
        value,
        true,
        change,
        value === selectedBackgroundColor
      )
    );
    if (selectedBackgroundColor !== "transparent") {
      return [
        createBackgroundSelectorItem(
          "reset",
          "Clear background",
          "transparent",
          null,
          change,
          false
        ),
        ...backgroundSelectorItems
      ];
    }
    return backgroundSelectorItems;
  }
);
var DEFAULT_BACKGROUNDS_CONFIG = {
  default: null,
  disable: true,
  values: []
};
var BackgroundSelector = memo(function BackgroundSelector2() {
  const backgroundsConfig = useParameter(
    PARAM_KEY,
    DEFAULT_BACKGROUNDS_CONFIG
  );
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [globals, updateGlobals] = useGlobals();
  const globalsBackgroundColor = globals[PARAM_KEY]?.value;
  const selectedBackgroundColor = useMemo(() => {
    return getBackgroundColorByName(
      globalsBackgroundColor,
      backgroundsConfig.values,
      backgroundsConfig.default
    );
  }, [backgroundsConfig, globalsBackgroundColor]);
  if (Array.isArray(backgroundsConfig)) {
    logger.warn(
      "Addon Backgrounds api has changed in Storybook 6.0. Please refer to the migration guide: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md"
    );
  }
  const onBackgroundChange = useCallback(
    (value) => {
      updateGlobals({ [PARAM_KEY]: { ...globals[PARAM_KEY], value } });
    },
    [backgroundsConfig, globals, updateGlobals]
  );
  if (backgroundsConfig.disable) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Fragment, null, /* @__PURE__ */ React.createElement(
    WithTooltip,
    {
      placement: "top",
      closeOnOutsideClick: true,
      tooltip: ({ onHide }) => {
        return /* @__PURE__ */ React.createElement(
          TooltipLinkList,
          {
            links: getDisplayedItems(
              backgroundsConfig.values,
              selectedBackgroundColor,
              ({ selected }) => {
                if (selectedBackgroundColor !== selected) {
                  onBackgroundChange(selected);
                }
                onHide();
              }
            )
          }
        );
      },
      onVisibleChange: setIsTooltipVisible
    },
    /* @__PURE__ */ React.createElement(
      IconButton,
      {
        key: "background",
        title: "Change the background of the preview",
        active: selectedBackgroundColor !== "transparent" || isTooltipVisible
      },
      /* @__PURE__ */ React.createElement(Icon.Photo, null)
    )
  ));
});
var GridSelector = memo(function GridSelector2() {
  const [globals, updateGlobals] = useGlobals();
  const { grid } = useParameter(PARAM_KEY, {
    grid: { disable: false }
  });
  if (grid?.disable) {
    return null;
  }
  const isActive = globals[PARAM_KEY]?.grid || false;
  return /* @__PURE__ */ React.createElement(
    IconButton,
    {
      key: "background",
      active: isActive,
      title: "Apply a grid to the preview",
      onClick: () => updateGlobals({
        [PARAM_KEY]: { ...globals[PARAM_KEY], grid: !isActive }
      })
    },
    /* @__PURE__ */ React.createElement(Icon.Grid, null)
  );
});

// src/manager.tsx
addons.register(ADDON_ID, () => {
  addons.add(ADDON_ID, {
    title: "Backgrounds",
    type: types.TOOL,
    match: ({ viewMode }) => !!(viewMode && viewMode.match(/^(story|docs)$/)),
    render: () => /* @__PURE__ */ React.createElement(Fragment, null, /* @__PURE__ */ React.createElement(BackgroundSelector, null), /* @__PURE__ */ React.createElement(GridSelector, null))
  });
});
